import { useState } from 'react';
import {
  Badge,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

export function Audit() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const state = useAsync(
    () =>
      Promise.all([
        adminApi.operations.auditPage({
          page,
          pageSize: 20,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(entityType.trim() ? { entityType: entityType.trim() } : {}),
        }),
        adminApi.operations.exceptions(),
      ]),
    [page, search, entityType]
  );
  if (state.loading) return <SkeletonList count={6} />;
  if (state.error || !state.data) {
    return (
      <Card>
        <span className="dsp-field__error">{state.error ?? 'Could not load audit data'}</span>
      </Card>
    );
  }
  const [audit, exceptions] = state.data;
  const exceptionCount =
    exceptions.credentials.length +
    exceptions.failedNotifications.length +
    exceptions.cancelledShifts.length;

  return (
    <div>
      <PageHeader
        title="Audit & exceptions"
        subtitle="Sensitive changes and operational follow-up"
      />
      <Card style={{ marginBottom: 16 }}>
        <strong>Exception queue</strong>
        <p className="dsp-muted" style={{ margin: '4px 0 10px' }}>
          {exceptionCount} item(s) need review across credentials, delivery, and cancellations.
        </p>
        <div className="dsp-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <Badge tone={exceptions.credentials.length ? 'warning' : 'success'}>
            Credentials {exceptions.credentials.length}
          </Badge>
          <Badge tone={exceptions.failedNotifications.length ? 'danger' : 'success'}>
            Failed delivery {exceptions.failedNotifications.length}
          </Badge>
          <Badge tone={exceptions.cancelledShifts.length ? 'neutral' : 'success'}>
            Cancelled shifts {exceptions.cancelledShifts.length}
          </Badge>
        </div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Row gap={12} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Search audit events">
            <Input
              type="search"
              value={search}
              placeholder="Action, entity type, or ID"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="Entity type">
            <Input
              value={entityType}
              placeholder="e.g. credential"
              onChange={(event) => {
                setEntityType(event.target.value);
                setPage(1);
              }}
            />
          </Field>
        </Row>
      </Card>

      {audit.items.length === 0 ? (
        <EmptyState
          title="No audit events"
          description="Sensitive changes will be recorded here."
        />
      ) : (
        <Stack gap={8}>
          {audit.items.map((event) => (
            <Card key={event.id}>
              <div className="dsp-row" style={{ justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <strong>{humanize(event.action)}</strong>
                  <div className="dsp-muted" style={{ fontSize: '0.84rem' }}>
                    {humanize(event.entityType)}
                    {event.entityId ? ` · ${event.entityId}` : ''}
                  </div>
                </div>
                <span className="dsp-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  {new Date(event.createdAt).toLocaleString()}
                </span>
              </div>
            </Card>
          ))}
          <Pagination
            {...audit}
            itemLabel="audit events"
            disabled={state.loading}
            onPageChange={setPage}
          />
        </Stack>
      )}
    </div>
  );
}
